import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { corDoFundo, ehClara } from '../../styles/contraste';

// Breadcrumb ("Início / Comunicação / Galeria de fotos") montado sozinho a
// partir do menu do site (layoutmenu, no Squidex): a página é achada pela rota,
// e o grupo é o item do menu onde ela está. Nada a preencher por página.
//
// A cor também é sozinha: mede o fundo que está de fato atrás dela e escolhe
// texto escuro ou claro pelo contraste APCA, como a capa dos relatórios. Sobre
// foto não há cor para medir -- aí a página passa `tom` ("claro" ou "escuro").
//
// `rota` serve para página filha que não está no menu (o álbum aponta a
// galeria); `atual` é o último item, sem link (o título do álbum).

const Lista = styled.ol`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ $tom }) => ($tom === 'escuro' ? 'rgba(255, 255, 255, 0.72)' : '#6f7a73')};

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  li + li::before {
    content: '/';
    opacity: 0.7;
  }

  /* Link na cor do texto, com sublinhado só ao passar o mouse; a página atual
     fica mais escura. O "&&" dá peso a mais: página com reset de link (a galeria tem
     "a { text-decoration: none }") apagava o sublinhado do hover. */
  && a {
    color: ${({ $tom }) => ($tom === 'escuro' ? '#fff' : 'inherit')};
    font-weight: 500;
    text-decoration: underline;
    text-decoration-color: transparent;
    text-underline-offset: 3px;
    transition: text-decoration-color 0.15s;
  }

  && a:hover {
    text-decoration-color: currentColor;
  }

  [aria-current='page'] {
    color: ${({ $tom }) => ($tom === 'escuro' ? 'rgba(255, 255, 255, 0.85)' : '#183b2c')};
  }

  /* Área de toque maior que o texto (no dedo, 18px de altura era pouco), sem
     mudar o espaço que o link ocupa. */
  && a {
    padding: 0.55rem 0;
    margin: -0.55rem 0;
  }

  /* Celular: uma linha só. Título longo de álbum ou notícia quebrava e deixava
     a "/" solta no começo da linha de baixo; agora o último item encurta com
     reticências e os de antes ficam inteiros. */
  @media (max-width: 720px) {
    flex-wrap: nowrap;

    li {
      flex-shrink: 0;
    }

    /* O grupo do menu ("Comunicação", "Institucional") não tem link: no
       celular sai, para sobrar espaço para o título (no álbum sobrava "D."). */
    li:not(:last-child):not(:has(a)) {
      display: none;
    }

    li:last-child {
      flex-shrink: 1;
      min-width: 0;
    }

    li:last-child > * {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const mesmaRota = (a = '', b = '') => a.replace(/\/+$/, '').toLowerCase() === b.replace(/\/+$/, '').toLowerCase();

const caminhoNoMenu = (menu = [], rota) => {
  for (const item of menu) {
    const grupo = item?.menu || item;
    const sub = (grupo?.subMenu || []).find((s) => mesmaRota(s.rotaSubmenu, rota));
    if (sub) return [{ rotulo: grupo.tituloMenu }, { rotulo: sub.tituloSubmenu, para: sub.rotaSubmenu }];
    if (grupo?.rota && mesmaRota(grupo.rota, rota)) return [{ rotulo: grupo.tituloMenu, para: grupo.rota }];
  }
  return [];
};

export const Trilha = ({ rota, atual, tom, className }) => {
  const { menu } = useHeaderStates();
  const { pathname } = useLocation();
  const lista = useRef(null);
  const [medido, setMedido] = useState('claro');

  const itens = [{ rotulo: 'Início', para: '/' }, ...caminhoNoMenu(menu, rota || pathname)];
  if (atual) itens.push({ rotulo: atual });

  useLayoutEffect(() => {
    if (!tom && lista.current) setMedido(ehClara(corDoFundo(lista.current)) ? 'claro' : 'escuro');
  }, [tom, menu]);

  if (itens.length < 2) return null;
  const ultimo = itens.length - 1;

  return (
    <nav aria-label="Você está em" className={className}>
      <Lista ref={lista} $tom={tom || medido}>
        {itens.map((item, i) => (
          <li key={`${item.rotulo}-${i}`}>
            {i === ultimo ? (
              <span aria-current="page">{item.rotulo}</span>
            ) : item.para ? (
              <Link to={item.para}>{item.rotulo}</Link>
            ) : (
              <span>{item.rotulo}</span>
            )}
          </li>
        ))}
      </Lista>
    </nav>
  );
};
